// Clicking a player's name anywhere in the app deals their trading card onto the screen.
//
// Before this, a name in a list was a link to the full profile page and nothing more, which on
// a phone meant a full navigation to read a jersey number. The card is the quick look; the
// profile is still one tap away from inside it, and the link still works with JavaScript off,
// because the trigger is a real anchor that this module intercepts rather than replaces.
//
// A native <dialog> does the unglamorous work — focus trap, Escape, inertness of the page
// behind — so the only thing written here is the part worth writing: the card arriving like one
// dealt across a table, and leaving like one flicked away.
import { prepareCards } from './card-flip.js';

const DEAL_MS = 420;
const TOSS_MS = 320;

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function ensureDialog() {
  let dialog = document.getElementById('player-card-dialog');
  if (dialog) return dialog;
  dialog = document.createElement('dialog');
  dialog.id = 'player-card-dialog';
  dialog.className = 'card-dialog';
  dialog.innerHTML =
    '<div class="card-dialog-stage"><div class="card-dialog-slot" aria-live="polite"></div></div>' +
    '<button class="card-dialog-close" type="button" data-card-close></button>';
  document.body.append(dialog);
  return dialog;
}

export function initPlayerCardModal(root = document, { fetchImpl = fetch, strings = null } = {}) {
  let busy = null;
  const text =
    strings ||
    (() => {
      try {
        return JSON.parse(root.getElementById?.('player-card-strings')?.textContent || '{}');
      } catch {
        return {};
      }
    })();

  async function open(trigger, childId) {
    if (busy === childId) return;
    busy = childId;
    const dialog = ensureDialog();
    const slot = dialog.querySelector('.card-dialog-slot');
    const close = dialog.querySelector('[data-card-close]');
    close.setAttribute('aria-label', text.close || 'Close');
    slot.innerHTML = '<p class="card-dialog-loading">' + (text.loading || '…') + '</p>';
    dialog.setAttribute('aria-label', trigger.textContent.trim());
    if (!dialog.open) dialog.showModal();

    let html;
    try {
      const res = await fetchImpl('/children/' + encodeURIComponent(childId) + '/card', {
        credentials: 'same-origin',
        headers: { 'x-requested-with': 'fetch' },
      });
      if (!res.ok) throw new Error(String(res.status));
      html = await res.text();
    } catch {
      busy = null;
      slot.innerHTML =
        '<p class="card-dialog-loading">' + (text.error || 'Could not load.') + '</p>';
      return;
    }
    if (busy !== childId) return; // a second name was clicked while this one was in flight
    busy = null;
    slot.innerHTML = html;
    prepareCards(slot);
    // Deal it in: the card starts low, small and turned, then lands square. The overshoot is
    // in the easing rather than a second keyframe, so it reads as weight and not as a bounce.
    const card = slot.querySelector('.player-card');
    if (card && !reduced()) {
      card.animate(
        [
          { transform: 'translate3d(38vw, 55vh, 0) rotate(26deg) scale(0.6)', opacity: 0 },
          { transform: 'none', opacity: 1 },
        ],
        { duration: DEAL_MS, easing: 'cubic-bezier(0.17, 0.84, 0.28, 1.06)', fill: 'both' }
      );
    }
    dialog.dispatchEvent(new CustomEvent('cardready', { detail: { childId } }));
  }

  async function dismiss(dialog) {
    const card = dialog.querySelector('.player-card');
    if (card && !reduced()) {
      // Flicked away, the direction it came from, so opening and closing feel like one gesture.
      await card
        .animate(
          [
            { transform: 'none', opacity: 1 },
            { transform: 'translate3d(30vw, 60vh, 0) rotate(20deg) scale(0.65)', opacity: 0 },
          ],
          { duration: TOSS_MS, easing: 'cubic-bezier(0.5, 0, 0.9, 0.4)', fill: 'both' }
        )
        .finished.catch(() => {});
    }
    dialog.close();
  }

  root.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-player-card]');
    if (trigger) {
      // Let a modified click (new tab, download) go to the profile page as the user asked.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      open(trigger, trigger.dataset.playerCard);
      return;
    }
    const dialog = event.target.closest('.card-dialog');
    if (!dialog) return;
    // The close button, or the backdrop around the card. Clicks on the card itself fall through
    // to the flip handler, which is what makes the whole card flip.
    if (event.target.closest('[data-card-close]') || !event.target.closest('.player-card'))
      dismiss(dialog);
  });

  // Escape fires `cancel` before `close`; intercept it so the card is tossed rather than cut.
  root.addEventListener('cancel', (event) => {
    const dialog = event.target.closest?.('.card-dialog');
    if (!dialog || reduced()) return;
    event.preventDefault();
    dismiss(dialog);
  });
}
